import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from './Style.module.css';
import PrimaryButton from '../../Components/Buttons/PrimaryButton';
import Loader from '../../Components/Loader/Loader';
import SearchIcon from '../../Components/Icons/SearchIcon';
import FilterIcon from '../../Components/Icons/FilterIcon';
import DotIcon from '../../Components/Icons/DotIcon';
import RepeatBookingIcon from '../../Components/Icons/RepeatBookingIcon';
import { getClientList } from '../../APICalling/APICalling';
import { getInitials } from '../../Utils/utils';
import IconMember from '../../Assets/images/IconMember.svg';
import CustomerDetailsSidebar from '../../Components/CustomerDetailsModal/CustomerDetailsModal';
import AddClientSidebar from '../../Components/CustomerDetailsModal/AddClientSidebar';
import ClientFilterSidebar from '../../Components/CustomerDetailsModal/ClientFilterSidebar';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CrossIcon from '../../Assets/images/Search Input.svg';
import Echo from '../../Components/Sockect/Echo';
import { buildCustomerListRequest } from '../../Utils/customerSearchUtils';
import logger from '../../Utils/logger';

// Generate consistent color based on user ID or name
const getConsistentColor = (userId, name = '') => {
    // Use user ID if available, otherwise use name
    const seed = userId ? String(userId) : (name || 'default');
    
    // Simple hash function to convert string to number
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Generate consistent RGB values with better visibility (darker, more saturated)
    // Range: 80-200 (more visible than 127-254)
    const r = Math.abs(hash % 120) + 80;
    const g = Math.abs((hash * 7) % 120) + 80;
    const b = Math.abs((hash * 13) % 120) + 80;
    
    return `rgb(${r},${g},${b})`;
};

const hasActiveClientFilters = (filters) => {
    if (!filters || Object.keys(filters).length === 0) return false;

    if (filters.clientProfileCreated && filters.clientProfileCreated !== 'All Time') return true;
    if (filters.hadFirstAppointment && filters.hadFirstAppointment !== 'All Time') return true;
    if (filters.hadAppointment && filters.hadAppointment !== 'All Time') return true;
    if (Array.isArray(filters.hadAppointmentWith) && filters.hadAppointmentWith.length > 0) return true;
    if (filters.hadAppointmentWithEmpty) return true;
    if (filters.futureAppointment && filters.futureAppointment !== '') return true;
    if (filters.hadService && filters.hadService !== '') return true;
    if (Array.isArray(filters.hadServiceInCategory) && filters.hadServiceInCategory.length > 0) return true;
    if (filters.membershipStatus !== '' && filters.membershipStatus !== undefined && filters.membershipStatus !== null) return true;
    if (filters.gender && filters.gender !== 'Any gender') return true;
    if (filters.referredBy && filters.referredBy !== '') return true;
    if (filters.tag && filters.tag !== '') return true;
    if (filters.lastAppointment === true) return true;
    if (filters.daysAgo && filters.daysAgo !== '') return true;
    if (filters.includeNoAppointments === true) return true;
    return false;
};

const getMembershipStatusKey = (value) => {
    if (value === undefined || value === null) return '';
    const normalized = String(value).trim().toLowerCase();
    if (normalized === '1' || normalized === 'active') return 'active';
    if (normalized === '0' || normalized === 'inactive') return 'inactive';
    if (normalized === '2' || normalized === 'expired') return 'expired';
    return normalized;
};

// Global cache outside component to persist across navigation (but clear on refresh)
let globalClientCache = {
    data: null,
    etag: null
};

const Clients = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // Initialize from global cache to persist across navigation
    // MUST be declared before useState so we can use it for initial state
    // Clear ETag on mount so the first fetch always gets fresh data
    // (balance changes from admin panel, other pages, etc. won't be
    // reflected if we send a stale ETag and get 304).
    const lastETag = useRef(null);
    const cachedCustomerList = useRef(globalClientCache.data);

    const [loading, setLoading] = useState(false);
    // Initialize initialLoading based on whether we have cached data
    const [initialLoading, setInitialLoading] = useState(!cachedCustomerList.current);
    const [isSideBarActive, setIsSideBarActive] = useState(false);
    // Correctly extract the list from the cached response object, matching the structure used in fetchCustomerList
    const [customerList, setCustomerList] = useState(cachedCustomerList.current?.data?.data?.list?.clients || []);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isAddClientOpen, setIsAddClientOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({});
    const [hasDataUpdate, setHasDataUpdate] = useState(false);
    const tableRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const isInitialMountRef = useRef(true);
    const lastUrlSearchProcessed = useRef(null);
    const appliedFiltersRef = useRef({});
    const fetchCustomerListRef = useRef(null);
    const searchRef = useRef('');
    const searchInputRef = useRef(null);
    const nextCursorRef = useRef(null);
    const pageCursorsRef = useRef([]);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const resetPaginationState = useCallback(() => {
        setPage(1);
        nextCursorRef.current = null;
        pageCursorsRef.current = [];
        setHasMore(true);
    }, []);

    // Helper function to convert predefined date ranges to actual date ranges
    const convertDateRange = (dateRangeText) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        switch (dateRangeText) {
            case `Today (${getCurrentDateInfo().today})`:
                return {
                    start: today.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: today.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };
            
            case `Yesterday (${getCurrentDateInfo().yesterday})`:
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                return {
                    start: yesterday.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: yesterday.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };
            
            case `This Week (${getCurrentDateInfo().thisWeek})`:
                const startOfWeek = new Date(today);
                startOfWeek.setDate(today.getDate() - today.getDay());
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return {
                    start: startOfWeek.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: endOfWeek.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };
            
            case `Last Week (${getCurrentDateInfo().lastWeek})`:
                const lastWeekStart = new Date(today);
                lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                return {
                    start: lastWeekStart.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: lastWeekEnd.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };
            
            case `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})`:
                const twoWeeksStart = new Date(today);
                twoWeeksStart.setDate(today.getDate() - 14);
                return {
                    start: twoWeeksStart.toLocaleDateString('en-GB').split('/').reverse().join('-'),
                    end: today.toLocaleDateString('en-GB').split('/').reverse().join('-')
                };
            
            case 'July 2025':
                return {
                    start: '01-07-2025',
                    end: '31-07-2025'
                };
            
            case 'June 2025':
                return {
                    start: '01-06-2025',
                    end: '30-06-2025'
                };
            
            case 'May 2025':
                return {
                    start: '01-05-2025',
                    end: '31-05-2025'
                };
            
            default:
                return null;
        }
    };

    // Helper function to get current date info
const getCurrentDateInfo = () => {
    const now = new Date();
    const today = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    // Get week info - create separate date objects to avoid mutation
    // For Monday to Sunday week format
    const thisWeekStart = new Date(now);
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days to Monday
    thisWeekStart.setDate(now.getDate() - daysFromMonday); // Start of this week (Monday)
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6); // End of this week (Sunday)
    const thisWeek = `${thisWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${thisWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    
    // Last week: 7 days before this week's start
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
    const lastWeek = `${lastWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    
    // Last two weeks: from 2 weeks ago to end of last week
    const twoWeeksAgoStart = new Date(lastWeekStart);
    twoWeeksAgoStart.setDate(lastWeekStart.getDate() - 7); // 7 days before last week's start
    const lastTwoWeeks = `${twoWeeksAgoStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    
    // Get dynamic month names for last 3 months
    const currentMonth = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    return { today, yesterday, thisWeek, lastWeek, lastTwoWeeks, currentMonth, previousMonth, twoMonthsAgo };
};

    // Helper function to highlight search text
    const highlightSearchText = (text, searchTerm) => {
        if (!searchTerm || !text) return text;
        
        // Convert to string and trim whitespace
        const textStr = String(text).trim();
        const searchStr = String(searchTerm).trim();
        
        if (!textStr || !searchStr) return textStr;
        
        // Escape special regex characters
        const escapedSearchTerm = searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
        
        // Check if there's a match
        if (!regex.test(textStr)) {
            return textStr;
        }
        
        // Split text by matches and rebuild with highlighting
        const parts = textStr.split(regex);
        
        return parts.map((part, index) => {
            // Check if this part matches the search term (case insensitive)
            if (part && new RegExp(`^${escapedSearchTerm}$`, 'gi').test(part)) {
                return (
                    <strong key={`highlight-${index}`} style={{ fontWeight: 'bold' }}>
                        {part}
                    </strong>
                );
            }
            return part || '';
        }).filter(part => part !== '');
    };

    const fetchCustomerList = useCallback(async (cursor = null, searchText = '', filters = {}) => {
        try {
            const isFirstPage = !cursor;

            // Show loader for all API calls (first page and pagination)
            setLoading(true);
            if (isFirstPage) {
                setHasDataUpdate(false);
            }

            const { item } = buildCustomerListRequest(searchText, cursor);

            // Add filters
            if (filters.clientProfileCreated && filters.clientProfileCreated !== 'All Time') {
                if (filters.clientProfileCreated === 'Custom Time Period' && filters.clientProfileStartDate && filters.clientProfileEndDate) {
                    // Convert ISO dates to DD-MM-YYYY format
                    const startDate = new Date(filters.clientProfileStartDate).toLocaleDateString('en-GB').split('/').reverse().join('-');
                    const endDate = new Date(filters.clientProfileEndDate).toLocaleDateString('en-GB').split('/').reverse().join('-');
                    item.profile_daterange = `${startDate} / ${endDate}`;
                } else {
                    // Convert predefined date ranges to actual date ranges
                    const dateRange = convertDateRange(filters.clientProfileCreated);
                    if (dateRange) {
                        item.profile_daterange = `${dateRange.start} / ${dateRange.end}`;
                    }
                }
            }

            if (filters.hadFirstAppointment && filters.hadFirstAppointment !== 'All Time') {
                if (filters.hadFirstAppointment === 'Custom Time Period' && filters.hadFirstStartDate && filters.hadFirstEndDate) {
                    // Convert ISO dates to DD-MM-YYYY format
                    const startDate = new Date(filters.hadFirstStartDate).toLocaleDateString('en-GB').split('/').reverse().join('-');
                    const endDate = new Date(filters.hadFirstEndDate).toLocaleDateString('en-GB').split('/').reverse().join('-');
                    item.first_appointment_daterange = `${startDate} / ${endDate}`;
                } else {
                    const dateRange = convertDateRange(filters.hadFirstAppointment);
                    if (dateRange) {
                        item.first_appointment_daterange = `${dateRange.start} / ${dateRange.end}`;
                    }
                }
            }

            if (filters.hadAppointment && filters.hadAppointment !== 'All Time') {
                if (filters.hadAppointment === 'Custom Time Period' && filters.hadAppointmentStartDate && filters.hadAppointmentEndDate) {
                    // Convert ISO dates to DD-MM-YYYY format
                    const startDate = new Date(filters.hadAppointmentStartDate).toLocaleDateString('en-GB').split('/').reverse().join('-');
                    const endDate = new Date(filters.hadAppointmentEndDate).toLocaleDateString('en-GB').split('/').reverse().join('-');
                    item.appointment_daterange = `${startDate} / ${endDate}`;
                } else {
                    const dateRange = convertDateRange(filters.hadAppointment);
                    if (dateRange) {
                        item.appointment_daterange = `${dateRange.start} / ${dateRange.end}`;
                    }
                }
            }

            if (filters.hadAppointmentWithEmpty) {
                item.appointment_with = '';
            } else if (filters.hadAppointmentWith && filters.hadAppointmentWith.length > 0) {
                // Send appointment_with IDs as a single comma-separated parameter
                item.appointment_with = filters.hadAppointmentWith.join(',');
            }

            if (filters.futureAppointment) {
                if (filters.futureAppointment === 'Has future appointment') {
                    item.future_appointment = true;
                } else if (filters.futureAppointment === 'Has no future appointment') {
                    item.no_future_appointment = true;
                }
            }

            if (filters.hadService) {
                // had_service expects comma-separated service IDs
                item.had_service = filters.hadService;
            }

            if (filters.hadServiceInCategory && filters.hadServiceInCategory.length > 0) {
                // service_category expects comma-separated category IDs
                item.service_category = filters.hadServiceInCategory.join(',');
            }

            if (filters.membershipStatus !== '' && filters.membershipStatus !== undefined && filters.membershipStatus !== null) {
                item.membership_status = String(filters.membershipStatus).toLowerCase();
            }

            if (filters.gender && filters.gender !== 'Any gender') {
                item.gender = filters.gender.toLowerCase();
            }

            if (filters.referredBy) {
                item.referred_by = filters.referredBy;
            }

            if (filters.tag) {
                item.tag = filters.tag;
            }

            if (filters.lastAppointment) {
                item.last_appointment = true;
            }

            if (filters.daysAgo) {
                item.days_ago = filters.daysAgo;
            }

            if (filters.includeNoAppointments) {
                item.include_no_appointments = true;
            }

            const response = await getClientList(item, isFirstPage ? lastETag.current : null, true);
            let responseData;
            
            // Check if it's an axios response object (has status)
            if (response && response.status) {
                if (response.status === 304) {
                    responseData = cachedCustomerList.current;
                } else {
                    // Standard success response (200)
                    responseData = response.data;
                    
                    // Update cache and ETag on first page only
                    if (isFirstPage && response.headers && response.headers['etag']) {
                        lastETag.current = response.headers['etag'];
                        cachedCustomerList.current = responseData;
                        
                        // Persist to Global Cache for navigation persistence
                        globalClientCache.etag = response.headers['etag'];
                        globalClientCache.data = responseData;
                    }
                }
            } else {
                // Fallback (should not happen with '0' force, but good for safety)
                responseData = response;
            }

            const allCustomer = responseData?.data?.data?.list?.clients || [];
            const paginationData = responseData?.data?.data?.list?.pagination;
            
            if (paginationData) {
                const hasMorePages =
                    paginationData?.hasMorePages ??
                    (paginationData?.currentPage < paginationData?.lastPage);
                setHasMore(!!hasMorePages);
                nextCursorRef.current = paginationData?.nextCursor ?? null;

                if (isFirstPage) {
                    pageCursorsRef.current = paginationData?.nextCursor
                        ? [paginationData.nextCursor]
                        : [];
                } else if (paginationData?.nextCursor) {
                    pageCursorsRef.current.push(paginationData.nextCursor);
                }
            }

            // For first page, replace the list; for subsequent pages, append
            if (isFirstPage) {
                setCustomerList(allCustomer);
            } else {
                setCustomerList(prevList => [...prevList, ...allCustomer]);
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    }, []);

    // Keep refs updated with latest values
    useEffect(() => {
        appliedFiltersRef.current = appliedFilters;
    }, [appliedFilters]);

    useEffect(() => {
        fetchCustomerListRef.current = fetchCustomerList;
    }, [fetchCustomerList]);

    useEffect(() => {
        searchRef.current = search;
    }, [search]);

    // Handle URL search parameter -> update local state and trigger search (similar to Bookings.js)
    useEffect(() => {
        const urlSearch = searchParams.get('search') || '';
        
        // Only process if:
        // 1. URL has a search parameter
        // 2. It's different from what we last processed
        if (urlSearch && lastUrlSearchProcessed.current !== urlSearch) {
            // Mark this URL search as processed
            lastUrlSearchProcessed.current = urlSearch;
            
            // Clear any pending debounced searches
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            
            // Always update the search state to reflect URL
            setSearch(urlSearch);
            
            // Add a small delay to ensure component is fully mounted and ready
            setTimeout(() => {
                // Trigger search for URL parameters
                setPage(1);
                // Don't clear list to avoid flash
                // setCustomerList([]); 
                nextCursorRef.current = null;
                pageCursorsRef.current = [];
                setHasMore(true);
                // Show initial loader for search-triggered fetch
                setInitialLoading(true);
                
                // Use current refs to get latest values and avoid stale closures
                const currentFilters = appliedFiltersRef.current;
                const currentFetch = fetchCustomerListRef.current;
                
                // Use appliedFilters to maintain filters with URL search
                if (currentFetch) {
                    currentFetch(null, urlSearch, currentFilters);
                }
            }, 500); // 500ms delay for smooth transition
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Fetch when filters change (skip initial mount)
    useEffect(() => {
        if (isInitialMountRef.current) {
            return; // Skip initial fetch, handled above
        }
        // Fetch when filters change (search is handled separately by debounced search)
        resetPaginationState();
        setInitialLoading(true);
        fetchCustomerList(null, search, appliedFilters);
    }, [appliedFilters, fetchCustomerList, resetPaginationState]);

    // Initial fetch on mount (when no URL search param) - only run once
    useEffect(() => {
        if (isInitialMountRef.current) {
            const urlSearch = searchParams.get('search');
            if (!urlSearch) {
                // No URL search, fetch normally after a delay to ensure refs are set
                setTimeout(() => {
                    const currentFetch = fetchCustomerListRef.current;
                    if (currentFetch) {
                        resetPaginationState();
                        setInitialLoading(!cachedCustomerList.current); // Use cache logic
                        currentFetch(null, '', appliedFiltersRef.current);
                    }
                }, 100);
            }
            isInitialMountRef.current = false;
        }
    }, [fetchCustomerList, resetPaginationState]);

    useEffect(() => {
        const handleScroll = () => {
            if (!tableRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 20 && !loading && hasMore && nextCursorRef.current) {
                setPage(prevPage => prevPage + 1);
                fetchCustomerList(nextCursorRef.current, search, appliedFilters);
            }
        };

        const table = tableRef.current;
        if (table) {
            table.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (table) {
                table.removeEventListener('scroll', handleScroll);
            }
        };
    }, [loading, hasMore, search, appliedFilters, fetchCustomerList]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Auto-focus search input on mount
    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    // Listen for customer deletion / update events
    useEffect(() => {
        const handleCustomerDeleted = () => {
            resetPaginationState();
            setCustomerList([]);
            fetchCustomerList(null, search, appliedFilters);
        };
        const handleCustomerListRefresh = async (event) => {
            const preserveCurrentPage = event?.detail?.preserveCurrentPage !== false;
            const targetPage = preserveCurrentPage ? Math.max(1, Number(page) || 1) : 1;
            const replayCursors = pageCursorsRef.current.slice(0, targetPage - 1);
            const previousScrollTop = tableRef.current?.scrollTop ?? 0;
            lastETag.current = null;
            cachedCustomerList.current = null;
            globalClientCache = {
                data: null,
                etag: null,
            };
            setHasDataUpdate(false);
            setPage(targetPage);
            setCustomerList([]);
            nextCursorRef.current = null;
            pageCursorsRef.current = [];
            setHasMore(true);
            setInitialLoading(true);
            await fetchCustomerList(null, search, appliedFilters);
            for (const cursor of replayCursors) {
                if (!cursor) break;
                // Rebuild list up to the current page so user keeps context.
                // eslint-disable-next-line no-await-in-loop
                await fetchCustomerList(cursor, search, appliedFilters);
            }
            if (preserveCurrentPage && targetPage > 1) {
                requestAnimationFrame(() => {
                    if (tableRef.current) {
                        tableRef.current.scrollTop = previousScrollTop;
                    }
                });
            }
        };

        const handleCustomerUpdated = (event) => {
            const updated = event?.detail?.customer;
            if (!updated?.id) return;
            setCustomerList(prevList => {
                if (!Array.isArray(prevList)) return prevList;
                const idx = prevList.findIndex(c => c?.id === updated.id);
                if (idx === -1) return prevList;
                const nextList = [...prevList];
                nextList[idx] = { ...nextList[idx], ...updated };
                return nextList;
            });
        };

        window.addEventListener('customerDeleted', handleCustomerDeleted);
        window.addEventListener('customerUpdated', handleCustomerUpdated);
        window.addEventListener('refreshClientList', handleCustomerListRefresh);

        return () => {
            window.removeEventListener('customerDeleted', handleCustomerDeleted);
            window.removeEventListener('customerUpdated', handleCustomerUpdated);
            window.removeEventListener('refreshClientList', handleCustomerListRefresh);
        };
    }, [search, appliedFilters, page, fetchCustomerList, resetPaginationState]);

    // Listen for socket events
    useEffect(() => {
        if (!Echo) return;

        const channelName = "cache-sync.users";
        let channel;

        try {
             channel = Echo.channel(channelName);
             if (typeof channel.subscribed === "function") {
                channel.subscribed(() => {
                   // console.log(`socket Successfully subscribed to ${channelName}`);
                });
             }

             // Listen to .data.updated event
             channel.listen('.data.updated', (data) => {
                logger.log('User created or updated', data);
                setHasDataUpdate(true);

                const action = data?.action;
                const payload = data?.data || {};
                const resourceId = data?.resource_id ?? payload?.id;

                if (!resourceId) {
                    return;
                }

                const isSearchActive = Boolean(searchRef.current?.trim());
                const isFilterActive = hasActiveClientFilters(appliedFiltersRef.current);
                const shouldSkipInsert = isSearchActive || isFilterActive;

                setCustomerList(prevList => {
                    if (!Array.isArray(prevList)) return prevList;

                    if (action === 'deleted') {
                        return prevList.filter(item => item?.id !== resourceId);
                    }

                    if (action === 'created') {
                        if (shouldSkipInsert) {
                            return prevList;
                        }
                        const exists = prevList.some(item => item?.id === resourceId);
                        if (exists) {
                            return prevList;
                        }
                        return [payload, ...prevList];
                    }

                    if (action === 'updated') {
                        const existingIndex = prevList.findIndex(item => item?.id === resourceId);
                        if (existingIndex === -1) {
                            if (shouldSkipInsert) {
                                return prevList;
                            }
                            return [payload, ...prevList];
                        }
                        const nextList = [...prevList];
                        nextList[existingIndex] = { ...nextList[existingIndex], ...payload };
                        return nextList;
                    }

                    return prevList;
                });
             });

        } catch (error) {
            console.error(`socket Error subscribing to ${channelName}`, error);
        }

        return () => {
             if (channel) {
                try {
                     if (typeof channel.stopListening === "function") {
                        channel.stopListening('.data.updated');
                     }
                      if (typeof Echo.leave === "function") {
                        Echo.leave(channelName);
                     }
                } catch (error) {
                    console.error(`socket Error leaving ${channelName}`, error);
                }
             }
        };

    }, []);

    // Sort customers by search relevance when there's a search term
    const filteredCustomers = React.useMemo(() => {
        if (!search.trim()) {
            return customerList;
        }

        const searchLower = search.toLowerCase().trim();
        
        return [...customerList].sort((a, b) => {
            const aName = `${a.name} ${a.lastname}`.toLowerCase();
            const bName = `${b.name} ${b.lastname}`.toLowerCase();
            const aContact = (a.contact_number || '').toLowerCase();
            const bContact = (b.contact_number || '').toLowerCase();
         
            // Priority 1: Exact match with full name
            if (aName === searchLower) return -1;
            if (bName === searchLower) return 1;

            // Priority 2: Name starts with search term
            const aNameStarts = aName.startsWith(searchLower);
            const bNameStarts = bName.startsWith(searchLower);
            if (aNameStarts && !bNameStarts) return -1;
            if (bNameStarts && !aNameStarts) return 1;

            // Priority 3: First name starts with search term
            const aFirstName = a.name.toLowerCase();
            const bFirstName = b.name.toLowerCase();
            const aFirstStarts = aFirstName.startsWith(searchLower);
            const bFirstStarts = bFirstName.startsWith(searchLower);
            if (aFirstStarts && !bFirstStarts) return -1;
            if (bFirstStarts && !aFirstStarts) return 1;

            // Priority 4: Contact number starts with search term
            const aContactStarts = aContact.startsWith(searchLower);
            const bContactStarts = bContact.startsWith(searchLower);
            if (aContactStarts && !bContactStarts) return -1;
            if (bContactStarts && !aContactStarts) return 1;

         

            // Priority 6: Name contains search term (word boundary)
            const aNameWordMatch = aName.includes(` ${searchLower}`) || aName.includes(`${searchLower} `);
            const bNameWordMatch = bName.includes(` ${searchLower}`) || bName.includes(`${searchLower} `);
            if (aNameWordMatch && !bNameWordMatch) return -1;
            if (bNameWordMatch && !aNameWordMatch) return 1;

            // Priority 7: Any field contains search term (email excluded)
            const aContains = aName.includes(searchLower) || aContact.includes(searchLower);
            const bContains = bName.includes(searchLower) || bContact.includes(searchLower);
            if (aContains && !bContains) return -1;
            if (bContains && !aContains) return 1;

            // Default: maintain original order
            return 0;
        });
    }, [customerList, search]);


    const closeSideBar = () => {
        setIsSideBarActive(false);
    }
    const openSideBar = () => {
        setIsSideBarActive(true);
    }

    const handleCustomerClick = (customer) => {
        setSelectedCustomer(customer);
        setIsSidebarOpen(true);
        // setSearch(''); 
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
        // Delay clearing customer so the slide-out animation can finish
        setTimeout(() => setSelectedCustomer(null), 350);
    };

    // Debounced search function
    const debouncedSearch = useCallback((searchValue) => {
        // Clear any existing timeout
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        
        // Set a new timeout for the search
        debounceTimerRef.current = setTimeout(() => {
            const trimmedValue = searchValue ? searchValue.trim() : '';
            
            // Only update URL - let the URL effect handle the API call to avoid duplicates
            const newSearchParams = new URLSearchParams(searchParams);
            if (trimmedValue) {
                newSearchParams.set('search', trimmedValue);
            } else {
                newSearchParams.delete('search');
            }
            
            // Update lastUrlSearchProcessed to mark this as processed (prevent URL effect from re-triggering)
            lastUrlSearchProcessed.current = trimmedValue;
            
            // Update URL
            setSearchParams(newSearchParams, { replace: true });
            
            // Directly call API without waiting for URL effect
            resetPaginationState();
            setInitialLoading(true);
            fetchCustomerList(null, trimmedValue, appliedFilters);
        }, 1000); // 1000ms (1 second) delay for better debouncing
    }, [appliedFilters, fetchCustomerList, searchParams, setSearchParams, resetPaginationState]); // Removed customerList.length

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        
        // Clear any existing timeout
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        
        // If search is empty, clear URL and search immediately
        if (value.trim() === '') {
            // Mark as processed to prevent URL effect from re-triggering
            lastUrlSearchProcessed.current = '';
            
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('search');
            setSearchParams(newSearchParams, { replace: true });
            
            resetPaginationState();
            setInitialLoading(true);
            fetchCustomerList(null, '', appliedFilters);
        } else {
            // Use debounced search for non-empty values
            debouncedSearch(value);
        }
    };

    const handleCustomerCreated = () => {
        // Refresh the customer list after a new customer is created
        resetPaginationState();
        setCustomerList([]);
        setSearch('');
        setInitialLoading(true); // Set initial loading for refresh
        fetchCustomerList(null, '', appliedFilters);
    };

    const handleApplyFilters = (filters) => {
        // console.log('Applied filters:', filters);
        setAppliedFilters(filters);
        // Redundant calls removed: useEffect on [appliedFilters] handles the fetch and state resets
    };

    const handleRefresh = () => {
        lastETag.current = null;
        cachedCustomerList.current = null;
        globalClientCache = {
            data: null,
            etag: null
        };
        setHasDataUpdate(false);
        resetPaginationState();
        setCustomerList([]);
        setInitialLoading(true);
        fetchCustomerList(null, search, appliedFilters);
    };

    const hasActiveFilters = () => hasActiveClientFilters(appliedFilters);

    return (
        <>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className={`${styles.bookingsContainer} header-sapce`}>
            <div className={`${styles.header} container-fluid`}>
                <div className={styles.leftSection}>
                    <button
                        type="button"
                        onClick={handleRefresh}
                        className={`${styles.refreshButton} ${(loading || initialLoading) ? styles.isRefreshing : ''}`}
                        title="Refresh client list"
                    >
                        <RepeatBookingIcon size={16} />
                        {hasDataUpdate && !(loading || initialLoading) && (
                            <span className={styles.updateIndicator}></span>
                        )}
                    </button>
                </div>
                    <div className={styles.rightSection}>
                        <div className={`${styles.InputGroup}`}>
                            <SearchIcon />
                            <input
                                ref={searchInputRef}
                                className={styles.searchInput}
                                type="text"
                                size={54}
                                placeholder="Search client name, number, email, membership ID"
                                value={search}
                                onChange={handleSearchChange}
                                autoComplete="nope"
                            />
                            {search.trim().length > 0 && (
                                <button
                                    className={styles.clearSearchButton}
                                    onClick={() => {
                                        // Clear debounce timer
                                        if (debounceTimerRef.current) {
                                            clearTimeout(debounceTimerRef.current);
                                            debounceTimerRef.current = null;
                                        }
                                        
                                        // Mark as processed to prevent URL effect from re-triggering
                                        lastUrlSearchProcessed.current = '';
                                        
                                        // Clear URL parameter
                                        const newSearchParams = new URLSearchParams(searchParams);
                                        newSearchParams.delete('search');
                                        setSearchParams(newSearchParams, { replace: true });
                                        
                                        setSearch('');
                                        resetPaginationState();
                                        setCustomerList([]);
                                        setInitialLoading(true); // Set initial loading for clear search
                                        fetchCustomerList(null, '', appliedFilters);
                                    }}
                                    type="button"
                                    title="Clear search"
                                >
                                    <img src={CrossIcon} alt="Clear" style={{ width: '16px', height: '16px' }} />
                                </button>
                            )}
                        </div>

                        {/* Desktop Actions */}
                        <div className={styles.desktopActions}>
                            <div 
                                className={`${styles.filterButton} ${styles.activeFilter}`}
                                onClick={() => setIsFilterOpen(true)}
                                style={{ cursor: 'pointer' }}
                            >
                                Options
                                <FilterIcon/>
                                {hasActiveFilters() && (
                                    <DotIcon size={10} color="#FF0000" className={styles.filterIndicator} />
                                )}
                            </div>
                            <PrimaryButton
                                className={styles.newCheckoutButton}
                                onClick={() => setIsAddClientOpen(true)}
                                text="Add Client"
                            />
                        </div>

                        {/* Mobile Hamburger Button */}
                        <button 
                            className={styles.hamburgerButton}
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-label="Menu"
                        >
                            <span className={styles.hamburgerLine}></span>
                            <span className={styles.hamburgerLine}></span>
                            <span className={styles.hamburgerLine}></span>
                        </button>

                        {/* Mobile Dropdown Menu */}
                        {isMobileMenuOpen && (
                            <div className={styles.mobileMenuDropdown}>
                                <div 
                                    className={`${styles.filterButton}`}
                                    onClick={() => {
                                        setIsFilterOpen(true);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Options
                                    <FilterIcon/>
                                    {hasActiveFilters() && (
                                        <DotIcon size={10} className={styles.filterIndicator} />
                                    )}
                                </div>
                                <PrimaryButton
                                    className={styles.newCheckoutButton}
                                    onClick={() => {
                                        setIsAddClientOpen(true);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    text="Add Client"
                                />
                            </div>
                        )}
                    </div>
            </div>
            <div className={`${styles.tableContainer} container-fluid`} ref={tableRef} style={{ maxHeight: 'calc(100vh - 76px - 70px - 15px)', overflowY: 'auto' }}>
                {initialLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <Loader />
                    </div>
                ) : (
                    <table>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10,backgroundColor: '#fff' }}>
                            <tr>
                                <th>Name</th>
                                <th>Phone</th>
                                {/* <th>Email</th> */}
                                <th>Cashless ID</th>
                                <th>Cashless Balance (S$)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCustomers.map((c, idx) => {
                                const membershipStatus = getMembershipStatusKey(c?.membership_status);
                                const membershipFlag = Number(c?.membership);
                                const hasCashlessId = Boolean(c?.membership_number);
                                // Source of truth: membership_status from API. Fallback to membership flag only when status is missing.
                                const isActiveMembership = membershipStatus
                                    ? membershipStatus === 'active'
                                    : membershipFlag === 1;
                                const isInactiveMembership = hasCashlessId && !isActiveMembership;
                                const idCellClassName = `${styles.colCashlessId} ${isInactiveMembership ? styles.colCashlessIdInactive : ''}`;
                                const balanceText = isActiveMembership && c.credit_balance != null
                                    ? Number(c.credit_balance).toFixed(2)
                                    : '-';

                                return (
                                    <tr key={`${c?.id ?? 'client'}-${idx}`} onClick={() => handleCustomerClick(c)} className={styles.clickableRow}>
                                        <td className={styles.colName}>
                                            <div className={styles.colNameContainer}>
                                                <div className={styles.profile} style={{backgroundColor: getConsistentColor(c.id, c.name)}}>
                                                    {getInitials(c.name)}
                                                    {isActiveMembership && (
                                                        <div className={styles.memberIcon}>
                                                            <img src={IconMember} alt="Member" />
                                                        </div>
                                                    )}
                                                </div>
                                                <span>
                                                    {highlightSearchText(c.name, search.trim())} {highlightSearchText(c.lastname || '', search.trim())}
                                                    {(c?.is_blocked === true || Number(c?.is_blocked) === 1) ? ' (Blocked)' : ''}
                                                </span>
                                            </div>
                                        </td>
                                        <td className={styles.colPhone}>{highlightSearchText(c.contact_number, search.trim())}</td>
                                        {/* <td className={styles.colEmail}>{highlightSearchText(c.email || "N/A", search.trim())}</td> */}
                                        <td className={idCellClassName}>
                                            <span
                                                className={isInactiveMembership ? styles.cashlessIdValueInactive : ''}
                                                title={isInactiveMembership ? 'expired' : undefined}
                                            >
                                                {c.membership_number || '-'}
                                            </span>
                                        </td>
                                        <td className={styles.colCashlessBalance}>{balanceText}</td>
                                    </tr>
                                );
                            })}
                            {filteredCustomers.length === 0 && !initialLoading && (
                                <tr>
                                    <td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>
                                        No Client found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
                
                {loading && !initialLoading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loadingMessage}>
                            Loading more clients...
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Customer Details Sidebar */}
        <CustomerDetailsSidebar 
            customer={selectedCustomer}
            isOpen={isSidebarOpen}
            onClose={closeSidebar}
        />
        <AddClientSidebar
            isOpen={isAddClientOpen}
            onClose={() => setIsAddClientOpen(false)}
            onCustomerCreated={handleCustomerCreated}
        />
        <ClientFilterSidebar
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilters={handleApplyFilters}
        />
        </>
    )
}

export default Clients
